import { type CSSProperties, type ReactNode } from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";

import { cn } from "@/shared/lib/cn";
import {
  getMerryButtonFigmaNodeId,
  type MerryButtonHierarchy,
  type MerryButtonInteractionState,
  type MerryButtonSize,
} from "@/shared/ui/merryButtonVariants";
import { useMerryButtonInteractionState } from "@/shared/ui/useMerryButtonInteractionState";

interface MerryButtonBaseProps
  extends Omit<AriaButtonProps, "children" | "className"> {
  className?: string;
  hierarchy?: MerryButtonHierarchy;
  size?: MerryButtonSize;
}

interface MerryButtonWithTextProps extends MerryButtonBaseProps {
  children: ReactNode;
  icon?: ReactNode;
}

interface MerryButtonIconOnlyProps extends MerryButtonBaseProps {
  "aria-label": string;
  children?: never;
  icon: ReactNode;
}

export type MerryButtonProps =
  | MerryButtonWithTextProps
  | MerryButtonIconOnlyProps;

export interface MerryButtonIconProps {
  className?: string;
  source34: string;
  source48: string;
}

type MerryButtonIconStyle = CSSProperties & {
  "--merry-button-icon-34": string;
  "--merry-button-icon-48": string;
};

function toCssUrl(source: string) {
  return `url(${JSON.stringify(source)})`;
}

export function MerryButtonIcon({
  className,
  source34,
  source48,
}: MerryButtonIconProps) {
  const style: MerryButtonIconStyle = {
    "--merry-button-icon-34": toCssUrl(source34),
    "--merry-button-icon-48": toCssUrl(source48),
  };

  return (
    <span
      aria-hidden="true"
      className={cn("merry-button-icon", className)}
      data-merry-button-icon=""
      style={style}
    />
  );
}

export function MerryButton({
  children,
  className,
  hierarchy = "level0",
  icon,
  isDisabled,
  onHoverChange,
  onPressChange,
  size = 48,
  type = "button",
  ...props
}: MerryButtonProps) {
  const hasText =
    children !== undefined && children !== null && children !== false;
  const contentVariant = hasText ? "text" : "iconOnly";
  const contentMode = hasText ? (icon ? "text-icon" : "text") : "icon";
  const interaction = useMerryButtonInteractionState({
    isDisabled,
    onHoverChange,
    onPressChange,
  });
  const interactionState: MerryButtonInteractionState =
    interaction.interactionState;

  return (
    <AriaButton
      {...props}
      className={({ isFocusVisible }) =>
        cn(
          "merry-button",
          isFocusVisible && "merry-button--focus-visible",
          isDisabled && "merry-button--disabled",
          className,
        )
      }
      data-button-content={contentMode}
      data-button-hierarchy={hierarchy}
      data-button-size={String(size)}
      data-button-state={interactionState}
      data-figma-component-id="299:31102"
      data-figma-node-id={getMerryButtonFigmaNodeId(
        interactionState,
        contentVariant,
        size,
        hierarchy,
      )}
      isDisabled={isDisabled}
      onHoverChange={interaction.onHoverChange}
      onPressChange={interaction.onPressChange}
      type={type}
    >
      <span className="merry-button__content">
        {icon ? <span className="merry-button__icon">{icon}</span> : null}
        {hasText ? <span className="merry-button__label">{children}</span> : null}
      </span>
    </AriaButton>
  );
}
